import type { StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import { useMemo, useState, type ComponentProps } from 'react'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { DatePickerDialog } from 'components/design-system/BreakoutDateTimeInput'
import { getRoomStateScheduledAtSchema } from '../roomStateScheduledAtSchema'
import { useDialogs } from 'hooks/dialogs'
import { useTranslationTyped } from 'i18n/i18n'
import { getScheduleRoomStateDatePickerDialogProps } from './ScheduleRoomStateDialogUtil'

export const ScheduleRoomStateButton = ({
  cubit,
  useInlineDialog,
  children,
  className,
  ...rest
}: Omit<ComponentProps<typeof BreakoutButton>, 'onClick' | 'loading'> & {
  useInlineDialog: boolean
  cubit: StudentAssignmentCubit
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { roomState } = cubit
  const { tt } = useTranslationTyped()
  const { showDialog, popDialog } = useDialogs()

  const minScheduleDate = cubit.assignment.minScheduleDate
  const maxScheduleDate = cubit.assignment.expiresAtDate

  const schema = useMemo(
    () =>
      getRoomStateScheduledAtSchema({
        tt,
        expiresAt: maxScheduleDate,
        availableFrom: cubit.assignment.assignedAtDate,
      }),
    [maxScheduleDate, tt, cubit.assignment.assignedAtDate]
  )

  const datePickerProps = getScheduleRoomStateDatePickerDialogProps({
    useInlineDialog,
    // Use libraryObject.scheduledAtDate (not roomState) so an incomplete
    // (mustScheduleSuspended) session clears the picker instead of pre-filling
    // the stale past date, which would fail validation on Save. See BL-4399.
    scheduledAtDate: cubit.libraryObject.scheduledAtDate,
    roomStateName: roomState.roomStateName,
    isOpen,
    minDate: minScheduleDate,
    maxDate: maxScheduleDate,
    cubit,
    schema,
    tt,
    popDialog,
    setIsOpen,
    setIsLoading,
  })

  return (
    <div className={className}>
      <BreakoutButton
        data-testid="schedule-room-state-button"
        loading={isLoading}
        onClick={(e) => {
          if (useInlineDialog) return setIsOpen(true)
          showDialog(() => <DatePickerDialog {...datePickerProps} />, {
            trigger: e.currentTarget,
          })
        }}
        {...rest}
      >
        {children}
      </BreakoutButton>
      {useInlineDialog && <DatePickerDialog {...datePickerProps} />}
    </div>
  )
}
